import type { Theme } from '@repo/shared/types/dashboard';
import { type REACT_WIDGET_RENDER_MESSAGE_FLAG, type REACT_WIDGET_RENDER_STAGES } from '@repo/shared/constants/core/widget';
import type { DEFAULT_HUB_CONFIGURATION_SKILL_SLUG_MAP } from '@repo/shared/constants/label-map/hub';

// Reference + credential the React-widget iframe uses to load the widget from
// the admin dashboard render route.
export interface ReactWidgetRenderContext {
	// Admin dashboard origin (e.g. https://dashboard.rapidflare.ai).
	renderBaseUrl: string;
	hubId: string;
	conversationId: string;
	messageId: string;
	// Bearer value (access token or embed API key); sent via postMessage, never in the URL.
	credential: string;
}

// One shape covers every message type on the widget postMessage channel
// ('ready' | 'credential' | 'code' | 'height' | 'error' | 'progress').
export interface RfWidgetRenderMessage {
	[REACT_WIDGET_RENDER_MESSAGE_FLAG]?: boolean;
	type?: string;
	credential?: string;
	code?: string;
	height?: number;
	message?: string;
	stage?: string;
	httpStatus?: number;
	// Echoed on every render-frame message; hosts correlate on this rather than
	// event.source, which changes across host remounts.
	artifactId?: string;
}

export interface WidgetRenderStageEntry {
	stage: typeof REACT_WIDGET_RENDER_STAGES[number];
	elapsedMs: number;
	httpStatus?: number;
}

// Outbound fields only: credentials and source code belong to the sandbox channel.
export type WidgetHostPayload =
	| { type: 'ready' | 'error'; }
	| ({ type: 'progress'; } & Required<Pick<RfWidgetRenderMessage, 'stage'>> & Pick<RfWidgetRenderMessage, 'httpStatus'>)
	| ({ type: 'height'; } & Required<Pick<RfWidgetRenderMessage, 'height'>>);

export interface WidgetRendererProps {
	hubId: string;
	conversationId: string;
	messageId: string;
	artifactId: string;
}

export enum RfEmbedSdkPosition {
	TOP = 'TOP',
	BOTTOM = 'BOTTOM',
	LEFT = 'LEFT',
	RIGHT = 'RIGHT'
}

export enum RfEmbedSdkAnchorPosition {
	TOP_LEFT = 'top-left',
	TOP_RIGHT = 'top-right',
	BOTTOM_LEFT = 'bottom-left',
	BOTTOM_RIGHT = 'bottom-right'
}

export enum RfEmbedSdkDisplayType {
	FLOATING_LAUNCHER = 'floating-launcher',
	FULL_PAGE = 'full-page',
	INLINE_TRIGGER = 'inline-trigger',
	SEARCH = 'search',
	AI_OVERVIEW = 'ai-overview'
}

export enum RfEmbedSdkInlineTriggerVariant {
	RIGHT_MODAL = 'right-modal',
	LEFT_MODAL = 'left-modal',
	CENTER_MODAL = 'center-modal',
	RIGHT_DRAWER = 'right-drawer',
	LEFT_DRAWER = 'left-drawer'
}

export enum RfEmbedSdkLegacyDisplayType {
	INLINE = 'inline',
	FLOATING = 'floating',
	SEARCH = 'search',
	MODAL = 'modal'
}

export enum RfEmbedSdkLegacyModalPosition {
	CENTER = 'center',
	LEFT = 'left',
	RIGHT = 'right'
}

// --- Display config sub-types (discriminated union members) ---

export interface RfEmbedSdkFloatingLauncherConfig {
	type: RfEmbedSdkDisplayType.FLOATING_LAUNCHER;
	position?: RfEmbedSdkAnchorPosition;
	trigger?: string | HTMLElement | SVGElement;
	dragContainer?: string;
	overlay?: boolean;
	// NOTE: When maximized, fill the width up to the same gutter the popup already uses today
	// (it stays a floating, absolutely-positioned surface — going fully edge-to-edge would look
	// glued to the viewport rather than intentionally widened).
	fullWidthOnMaximize?: boolean;
	titleText?: string;
	titleLogo?: string;
}

export interface RfEmbedSdkFullPageConfig {
	type: RfEmbedSdkDisplayType.FULL_PAGE;
	container: string | HTMLElement;
}

export interface RfEmbedSdkInlineTriggerConfig {
	type: RfEmbedSdkDisplayType.INLINE_TRIGGER;
	trigger: string | HTMLElement;
	variant?: RfEmbedSdkInlineTriggerVariant;
	overlay?: boolean;
	responsive?: boolean;
	// NOTE: When maximized, go fully edge-to-edge (100% width, no gutter) — unlike the floating
	// launcher, the drawer/modal surface is expected to cover the viewport when widened.
	fullWidthOnMaximize?: boolean;
	titleText?: string;
	titleLogo?: string;
}

// --- Search `display.action`: what happens when the user searches (on-page child or external URL) ---

export enum RfEmbedSdkSearchActionType {
	WIDGET = 'widget',
	EXTERNAL_URL = 'external-url'
}

/** External-url search action. Field names are `url` / `newTab`, nested under `action`. */
export interface RfEmbedSdkSearchExternalUrlAction {
	type: RfEmbedSdkSearchActionType.EXTERNAL_URL;
	url: string;
	newTab?: boolean;
}

export interface RfEmbedSdkSearchWidgetFloatingLauncherConfig {
	id?: string;
	type: RfEmbedSdkDisplayType.FLOATING_LAUNCHER;
	position?: RfEmbedSdkAnchorPosition;
	overlay?: boolean;
	fullWidthOnMaximize?: boolean;
	titleText?: string;
	titleLogo?: string;
}

export interface RfEmbedSdkSearchWidgetInlineTriggerConfig {
	id?: string;
	type: RfEmbedSdkDisplayType.INLINE_TRIGGER;
	variant?: RfEmbedSdkInlineTriggerVariant;
	overlay?: boolean;
	responsive?: boolean;
	fullWidthOnMaximize?: boolean;
	titleText?: string;
	titleLogo?: string;
}

export type RfEmbedSdkSearchWidgetConfig = RfEmbedSdkSearchWidgetFloatingLauncherConfig | RfEmbedSdkSearchWidgetInlineTriggerConfig;

export interface RfEmbedSdkSearchWidgetAction {
	type: RfEmbedSdkSearchActionType.WIDGET;
	widget: RfEmbedSdkSearchWidgetConfig;
}

export type RfEmbedSdkSearchAction = RfEmbedSdkSearchExternalUrlAction | RfEmbedSdkSearchWidgetAction;

export interface RfEmbedSdkSearchConfig {
	type: RfEmbedSdkDisplayType.SEARCH;
	container: string | HTMLElement;
	placeholder?: string;
	minRows?: number;
	maxRows?: number;
	autoFocus?: boolean;
	disableKeyboardTrigger?: boolean;
	aiSuggestionsDisabled?: boolean;
	starterPromptsDisabled?: boolean;
	starterPromptHeaderText?: string;
	starterPromptAutoSubmit?: boolean;
	// Canonical search behavior. Omit → default on-page conversation child (inline-trigger right-modal).
	action?: RfEmbedSdkSearchAction;
}

export interface RfEmbedSdkAiOverviewConfig {
	type: RfEmbedSdkDisplayType.AI_OVERVIEW;
	container: string | HTMLElement;
	titleText?: string;
	titleLogo?: string;
	ctaText?: string;
	ctaBackground?: string;
	ctaColor?: string;
	ctaUrl?: string;
	ctaNewTab?: boolean;
}

export type RfEmbedSdkDisplayConfig = {
	productCompareUrl?: string;
} & (
	| RfEmbedSdkFloatingLauncherConfig
	| RfEmbedSdkFullPageConfig
	| RfEmbedSdkInlineTriggerConfig
	| RfEmbedSdkSearchConfig
	| RfEmbedSdkAiOverviewConfig
);

/** The page the visitor came from, as the host page reports it. */
export interface RfEmbedSdkReferrerPage {
	url?: string;
	title?: string;
}

/**
 * Context the host page knows about the visitor's journey. `referrerPage` is supplied rather than
 * sniffed because `document.referrer` is empty whenever the link carried `rel="noopener noreferrer"`,
 * the page was opened via `window.open`, or a referrer policy stripped it on a cross-origin hop — and
 * it never carries a title at all. Flattened to markdown and sent to the agent as `ambient_context`.
 */
export interface RfEmbedSdkContext {
	referrerPage?: RfEmbedSdkReferrerPage;
	ambient?: string;
}

// --- Main SDK config ---

export interface RfEmbedSdkConfig {
	id?: string;
	apiKey?: string;
	display: RfEmbedSdkDisplayConfig;
	userId?: string;
	conversationId?: string;
	theme?: Theme;
	prompt?: string;
	context?: RfEmbedSdkContext;
	autoSubmit?: boolean;
	responsive?: boolean;
	doNotTrack?: boolean;
	// Which hub skill the widget runs. Defaults to `qa`. `product-selection` renders the catalog /
	// comparison instead of the conversation, configured entirely from the host page's own query
	// params. Typed off the slug map so it tracks the same source EmbedWidget validates against.
	agent?: typeof DEFAULT_HUB_CONFIGURATION_SKILL_SLUG_MAP[keyof typeof DEFAULT_HUB_CONFIGURATION_SKILL_SLUG_MAP];
	onLoad?: (ref: RfEmbedSdkWidgetRef) => void;
	onOpen?: () => void;
	onClose?: () => void;
	// Runs when the widget replaces itself with an error screen (bad `apiKey`, a hub / conversation /
	// catalog request that failed). The message is the same customer-safe copy shown on that screen,
	// never a backend diagnostic. An object rather than a bare string so fields can be added later
	// without breaking a host page that already destructures it.
	onError?: (error: RfEmbedSdkWidgetError) => void;
}

export interface RfEmbedSdkWidgetError {
	message: string;
	code?: string;
	details?: Record<string, string>;
}

/**
 * Legacy inputs a host may still hand `init()`, kept out of the canonical shapes above.
 * `resolveRfEmbedSdkConfig` normalizes each onto its canonical field, so none of them reaches the
 * config the widget runs on, or what `update()` accepts.
 *
 * @deprecated `ambientContext` → `context.ambient`; `searchUrl` / `searchNewTab` → `display.action`.
 */
export interface RfEmbedSdkConfigLegacyInput {
	ambientContext?: string;
	display?: RfEmbedSdkDisplayConfig & {
		searchUrl?: string;
		searchNewTab?: boolean;
	};
}

/** A search display as a host may still hand it over, carrying the legacy flat inputs. */
export type RfEmbedSdkLegacySearchDisplay = RfEmbedSdkSearchConfig & Pick<NonNullable<RfEmbedSdkConfigLegacyInput['display']>, 'searchUrl' | 'searchNewTab'>;

/** What a host may pass to `init()`: the canonical config, plus any legacy input still honored. */
export type RfEmbedSdkConfigInput = RfEmbedSdkConfig & RfEmbedSdkConfigLegacyInput;

export interface RfEmbedSdkConfigLegacy {
	apiKey?: string;
	userId?: string;
	conversationId?: string;
	theme?: Theme;
	prompt?: string;
	autoSubmit?: boolean;
	onLoad?: () => void;
	onOpen?: () => void;
	onClose?: () => void;
	position?: RfEmbedSdkAnchorPosition;
	widgetTriggerSelector?: string | HTMLElement | SVGElement | null;
	overlay?: boolean;
	container?: string | HTMLElement;
	aiModeConfig?: RfEmbedSdkAiModeConfigLegacy;
	displayModeConfig?: RfEmbedSdkDisplayModeLegacy;
}

export interface RfEmbedSdkInstanceState {
	config: RfEmbedSdkConfig;
	overrideConfig?: Partial<RfEmbedSdkConfig>;
	isOpen: boolean;
	isMaximized: boolean;
	hub: Record<string, unknown> | undefined;
	uiConfig: Record<string, unknown> | undefined;
	triggerElement: HTMLElement | SVGElement | null;
	containerElement: HTMLElement | null;
	dragContainerElement: HTMLElement | null;
	quoteCartOpen: boolean;
	aiOverviewExpanded: boolean;
}

// --- Widget ref ---

export interface RfEmbedSdkWidgetRef {
	readonly id: string;
	open(): void;
	close(): void;
	maximize(): void;
	minimize(): void;
	update(partial: Partial<RfEmbedSdkConfig>): void;
	resetConversation(): void;
	destroy(): void;
	config(): RfEmbedSdkConfig;
}

export interface DeferredRfEmbedSdkWidgetRef extends RfEmbedSdkWidgetRef {
	_resolved?: boolean;
	_element?: HTMLElement;
	_entry?: RfEmbedSdkQueueEntry;
}

export interface RfEmbedSdkQueueEntry {
	config: RfEmbedSdkConfig;
	ref: DeferredRfEmbedSdkWidgetRef | null;
	pendingOpen?: boolean;
	pendingMaximized?: boolean;
}

// --- Instance context ---

export interface RfEmbedSdkInstanceContextProps {
	instanceId?: string;
	config?: RfEmbedSdkConfig;
	overrideConfig?: Partial<RfEmbedSdkConfig>;
}

// --- Legacy types used by rf-embed-sdk pre-migration consumers ---

/**
 * Legacy `aiModeConfig` on `rapidflare.init()` (before the `display` schema).
 * Maps to `display.type: 'ai-overview'`. Both `overlay*` and `aiMode*` names are accepted in production configs.
 */
export interface RfEmbedSdkAiModeConfigLegacy {
	showOverlay?: boolean;
	showAiModeOverlay?: boolean;
	overlayTitleText?: string;
	overlayTitleLogo?: string;
	aiModeOverlayTitleText?: string;
	aiModeOverlayTitleLogo?: string;
	overlayButtonText?: string;
	overlayButtonColor?: string;
	overlayButtonBackground?: string;
	aiModeOverlayButtonText?: string;
	aiModeOverlayButtonColor?: string;
	aiModeOverlayButtonBackground?: string;
	buttonClickUrl?: string;
	buttonClickOpenNewTab?: boolean;
	aiModeButtonClickUrl?: string;
	aiModeButtonClickOpenNewTab?: boolean;
}

export interface RfEmbedSdkDisplayModeFloatingConfigLegacy {
	position?: RfEmbedSdkAnchorPosition;
	overlay?: boolean;
	widgetTriggerSelector?: string | HTMLElement | SVGElement | null;
	widgetTriggerElement?: HTMLElement | SVGElement | null;
	dragContainerSelector?: string | HTMLElement | null;
}

export interface RfEmbedSdkDisplayModeModalConfigLegacy {
	position?: RfEmbedSdkLegacyModalPosition;
	docked?: boolean;
	responsive?: boolean;
	overlay?: boolean;
	widgetTriggerSelector?: string | HTMLElement | SVGElement | null;
	title?: string;
	logo?: string;
}

export interface RfEmbedSdkDisplayModeSearchConfigLegacy {
	placeholder?: string;
	minRows?: number;
	maxRows?: number;
	autoFocus?: boolean;
	searchClickUrl?: string;
	searchClickOpenNewTab?: boolean;
	aiSuggestionsDisabled?: boolean;
	starterPromptsDisabled?: boolean;
	starterPromptHeaderText?: string;
	starterPromptAutoSubmit?: boolean;
}

export interface RfEmbedSdkDisplayModeLegacy {
	type: RfEmbedSdkLegacyDisplayType;
	floatingModeConfig?: RfEmbedSdkDisplayModeFloatingConfigLegacy;
	modalModeConfig?: RfEmbedSdkDisplayModeModalConfigLegacy;
	searchModeConfig?: RfEmbedSdkDisplayModeSearchConfigLegacy;
}

/** Keys on a display config whose property is optional and typed as string (excludes required fields like `type`). */
export type RfEmbedSdkOptionalStringFieldKey<TDisplayConfig extends RfEmbedSdkDisplayConfig> = {
	[FieldKey in keyof TDisplayConfig]-?: undefined extends TDisplayConfig[FieldKey] ? (TDisplayConfig[FieldKey] extends string | undefined ? FieldKey : never) : never;
}[keyof TDisplayConfig];

/** Keys on a display config whose property is optional and typed as boolean. */
export type RfEmbedSdkOptionalBooleanFieldKey<TDisplayConfig extends RfEmbedSdkDisplayConfig> = {
	[FieldKey in keyof TDisplayConfig]-?: undefined extends TDisplayConfig[FieldKey] ? (TDisplayConfig[FieldKey] extends boolean | undefined ? FieldKey : never) : never;
}[keyof TDisplayConfig];

export type RfEmbedSdkInstanceOverrideConfig = Partial<Omit<RfEmbedSdkConfig, 'display'> & { display: Partial<RfEmbedSdkDisplayConfig>; }>;
